import { useState, useEffect, useCallback } from 'react';
import { payHeadsApi, STATUS_OPTIONS } from '../api/payHeadsApi';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { appToast } from '../../../../components/AppToast';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

export const TYPE_FILTER_OPTIONS = [
  { value: 'All', label: 'All Types' },
  { value: 'Earning', label: 'Earnings' },
  { value: 'Deduction', label: 'Deductions' },
];

const REPORT_COLUMNS = [
  { key: 'name', label: 'Pay Head Name' },
  { key: 'pay_head_type', label: 'Type' },
  { key: 'calculation_type', label: 'Calculation Type' },
  { key: 'calculation_period', label: 'Frequency' },
  { key: 'status', label: 'Status' },
];

export function usePayHeadsList() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [typeFilter, setTypeFilter] = useState('All');

  const searchFieldOptions = [
    { value: 'Name', label: 'Name' },
    { value: 'Alias', label: 'Alias' },
  ];

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await payHeadsApi.getPayHeads({
        search: searchText.trim() || undefined,
        payHeadType: typeFilter !== 'All' ? typeFilter : undefined,
        status: statusFilter,
        page,
        pageSize,
      });

      setRecords(res.items || []);
      setTotalCount(res.totalCount || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch Pay Heads:', err);
      appToast.error('Failed to load Pay Heads.');
    } finally {
      setIsLoading(false);
    }
  }, [searchText, statusFilter, typeFilter, page, pageSize]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const resetFilters = () => {
    setSearchText('');
    setStatusFilter('Active');
    setTypeFilter('All');
    setPage(1);
  };

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Pay Head',
      message: `Are you sure you want to delete pay head "${name}"? This will soft delete the configuration.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await payHeadsApi.deletePayHead(id);
        appToast.success(`Pay Head "${name}" deleted successfully.`);
        fetchRecords();
      } catch (err) {
        console.error('Failed to delete pay head:', err);
        appToast.error(err?.response?.data?.detail?.message || 'Failed to delete Pay Head.');
      }
    }
  };

  const handleArchive = async (id, name, isCurrentlyArchived) => {
    const action = isCurrentlyArchived ? 'Restore' : 'Archive';
    const confirmed = await appConfirm({
      title: `${action} Pay Head`,
      message: `Are you sure you want to ${action.toLowerCase()} pay head "${name}"?`,
      confirmText: action,
      cancelText: 'Cancel',
      variant: isCurrentlyArchived ? 'primary' : 'warning',
    });

    if (confirmed) {
      try {
        await payHeadsApi.archivePayHead(id);
        appToast.success(`Pay Head "${name}" ${isCurrentlyArchived ? 'restored' : 'archived'} successfully.`);
        fetchRecords();
      } catch (err) {
        console.error('Failed to toggle archive status:', err);
        appToast.error('Failed to update Pay Head status.');
      }
    }
  };

  const { triggerExport, isExporting } = useExportJob({
    module: 'payheads',
    jobType: 'EXPORT',
  });

  const tools = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export Excel', icon: 'description' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
  ];

  const handleToolSelect = (toolKey) => {
    if (toolKey === 'print') {
      executePrintReport({
        title: 'Pay Head Master Report',
        subtitle: `Status: ${statusFilter} | Total Records: ${totalCount}`,
        columns: REPORT_COLUMNS,
        data: records,
        onExportPdf: () => triggerExport('pdf', { search: searchText, status: statusFilter }),
        onExportExcel: () => triggerExport('xlsx', { search: searchText, status: statusFilter }),
      });
    } else if (toolKey === 'export_excel') {
      triggerExport('xlsx', { search: searchText, status: statusFilter });
    } else if (toolKey === 'export_pdf') {
      triggerExport('pdf', { search: searchText, status: statusFilter });
    }
  };

  return {
    state: {
      records,
      isLoading,
      searchField,
      searchText,
      statusFilter,
      typeFilter,
      page,
      pageSize,
      totalPages,
      totalCount,
      searchFieldOptions,
      statusOptions: STATUS_OPTIONS,
      typeOptions: TYPE_FILTER_OPTIONS,
      tools,
      isExporting,
    },
    actions: {
      setSearchField,
      setSearchText,
      setStatusFilter: (val) => {
        setStatusFilter(val);
        setPage(1);
      },
      handleStatusTabChange,
      setTypeFilter: (val) => {
        setTypeFilter(val);
        setPage(1);
      },
      setPage,
      handleSearch,
      resetFilters,
      handleDelete,
      handleArchive,
      handleToolSelect,
      refresh: fetchRecords,
    },
  };
}
